import React, { useCallback, useMemo, useState } from 'react';
import { Search } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../../lib/utils';
import {
  baseTextSize,
  borderGrey,
  borderPrimary,
  placeholderPrimary,
  primaryText,
  rounded,
  secondaryText,
  semiBoldText,
  smallTextSize,
  tertiaryText,
  whiteBG,
} from '../../../styling/stylingHelper';
import { FrontendStaffUser } from '../../../services/staff/staffService';
import { useRestaurantStore } from '../../../stores/appRestaurantStore';
import { useAuth } from '../../../services/authentication/authContext';
import { useFiltersStore } from '../../../stores/appFiltersStore';
import { useStaffStore, AccessRequestStatus } from '../../../stores/appStaffStore';
import { useLoadStaff } from '../../../utils/dataLoadingUtils';
import { canManageStaff, canManageStaffMember } from '../../../services/accessManagement/accessManagement';
import SaveStaffUserModal from '../../../components/staff/SaveStaffUserModal';
import ManageStaffRestaurantsModal from '../../../components/staff/ManageStaffRestaurantsModal';
import ManageStaffAccessModal from '../../../components/staff/ManageStaffAccessModal';
import MobilePageHeader from '../../shared/mobile/MobilePageHeader';
import { StaffFilters, StaffUserCard, StaffDetailPanel, DeleteUserModal, RemoveAccessModal } from './components';
import { useFilteredStaff } from './hooks/useFilteredStaff';
import { useStaffMutations } from './hooks/useStaffMutations';

const StaffPage: React.FC = () => {
  const { t } = useTranslation();
  const { backendUser } = useAuth();
  const isAdmin = backendUser?.role === 'admin';

  const staff = useStaffStore((s) => s.staff);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  useLoadStaff();

  const restaurants = useRestaurantStore((s) => s.restaurants);
  const actorRestaurantIds = useMemo(() => restaurants.map((r) => r.id), [restaurants]);
  // Admin OR super-staff (manage_staff / all_permissions) may create staff.
  const canCreateStaff = canManageStaff(backendUser);
  const staffRestaurantFilters = useFiltersStore((s) => s.staffRestaurantFilters);
  const setStaffRestaurantFilters = useFiltersStore((s) => s.setStaffRestaurantFilters);

  const [statusFilter, setStatusFilter] = useState<AccessRequestStatus | 'all'>('all');
  const [isCreateOpen, setCreateOpen] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState<FrontendStaffUser | null>(null);
  const [isManageOpen, setManageOpen] = useState(false);
  const [deletingUser, setDeletingUser] = useState<FrontendStaffUser | null>(null);
  const [isDeleteOpen, setDeleteOpen] = useState(false);
  const [removingAccess, setRemovingAccess] = useState<{
    user: FrontendStaffUser;
    restaurantId: string;
    restaurantName: string;
  } | null>(null);
  const [isRemoveAccessOpen, setRemoveAccessOpen] = useState(false);
  const [accessModalUser, setAccessModalUser] = useState<FrontendStaffUser | null>(null);
  const [accessModalRestaurantId, setAccessModalRestaurantId] = useState<string | null>(null);
  const [isAccessOpen, setAccessOpen] = useState(false);

  const {
    error,
    reload,
    deleteUser,
    removeRestaurantAccess,
    togglePriceAlert,
    toggleWeeklyReports,
    toggleStaffSignupEmail,
    togglePermission,
  } = useStaffMutations();

  const getRestaurantName = useCallback(
    (id: string) => restaurants.find((r) => r.id === id)?.name || id,
    [restaurants]
  );

  const filteredAndSortedStaff = useFilteredStaff({
    staff,
    statusFilter,
    restaurantFilters: staffRestaurantFilters,
    searchQuery,
    getRestaurantName,
  });

  // Keep the panel open on a member who dropped out of the current filter, so
  // changing a filter mid-edit doesn't silently close the detail view.
  const selectedUser = useMemo(
    () => (selectedUserId ? staff.find((u) => u.id === selectedUserId) ?? null : null),
    [selectedUserId, staff]
  );

  const handleDeleteUserConfirm = async () => {
    if (!deletingUser) return;
    if (await deleteUser(deletingUser)) {
      if (selectedUserId === deletingUser.id) setSelectedUserId(null);
      setDeleteOpen(false);
    }
  };

  const handleRemoveAccessConfirm = async () => {
    if (!removingAccess) return;
    if (await removeRestaurantAccess(removingAccess.user, removingAccess.restaurantId)) {
      setRemoveAccessOpen(false);
    }
  };

  const renderDetailPanel = (user: FrontendStaffUser) => (
    <StaffDetailPanel
      user={user}
      restaurants={restaurants}
      isAdmin={isAdmin}
      canManage={canManageStaffMember(backendUser, user, actorRestaurantIds)}
      onClose={() => setSelectedUserId(null)}
      onDeleteUser={() => {
        setDeletingUser(user);
        setDeleteOpen(true);
      }}
      onRemoveRestaurantAccess={(rid, name) => {
        setRemovingAccess({ user, restaurantId: rid, restaurantName: name });
        setRemoveAccessOpen(true);
      }}
      onManageAccess={(rid) => {
        setAccessModalUser(user);
        setAccessModalRestaurantId(rid);
        setAccessOpen(true);
      }}
      onAddRestaurant={() => {
        setSelectedStaff(user);
        setManageOpen(true);
      }}
      onPermissionToggle={(perm, enabled) => togglePermission(user, perm, enabled)}
      onPriceAlertToggle={(enabled) => togglePriceAlert(user, enabled)}
      onWeeklyReportsToggle={(enabled) => toggleWeeklyReports(user, enabled)}
      onStaffSignupEmailToggle={(enabled) => toggleStaffSignupEmail(user, enabled)}
    />
  );

  return (
    <div>
      <MobilePageHeader title={t('staff.title')} />

      {/* Header */}
      <div className="mb-6 flex items-center justify-between gap-5">
        <div className="min-w-0 hidden lg:block">
          <h1 className={cn(semiBoldText, primaryText, 'text-[24px] leading-10')}>{t('staff.title')}</h1>
          <p className={cn(smallTextSize, secondaryText, 'mt-1')}>{t('staff.subtitle')}</p>
        </div>
        {canCreateStaff && (
          <button
            onClick={() => setCreateOpen(true)}
            className={cn(
              semiBoldText,
              'text-white dark:text-white',
              'bg-darkGreenColor dark:bg-darkGreenColor-dark',
              'text-[14px] leading-5 rounded-xl p-3',
              'hover:opacity-95 flex items-center gap-1.5 shrink-0 ml-auto'
            )}
          >
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden>
              <path d="M10 3.33398V16.6673" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
              <path d="M3.33398 10H16.6673" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span>{t('staff.addStaff')}</span>
          </button>
        )}
      </div>

      {error && (
        <div
          className={cn(
            'mb-6 p-3',
            rounded,
            'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400',
            smallTextSize
          )}
        >
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="mb-6">
        <StaffFilters
          restaurants={restaurants}
          statusFilter={statusFilter}
          onStatusFilterChange={setStatusFilter}
          staffRestaurantFilters={staffRestaurantFilters}
          onRestaurantFiltersChange={setStaffRestaurantFilters}
        />
      </div>

      {/* Master / detail */}
      <div
        className={cn(
          'grid gap-6 items-start',
          selectedUser ? 'lg:grid-cols-[minmax(0,1fr)_minmax(0,1.05fr)]' : 'grid-cols-1'
        )}
      >
        <div className={cn('min-w-0 p-4 rounded-xl', whiteBG)}>
          {/* Search */}
          <div className="relative mb-5">
            <Search className={cn('absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 pointer-events-none', tertiaryText)} />
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={t('staff.searchPlaceholder')}
              className={cn(
                'w-full h-[44px] pl-10 pr-3 rounded-xl border outline-none text-[16px] leading-6',
                borderPrimary,
                whiteBG,
                primaryText,
                placeholderPrimary
              )}
            />
          </div>

          <p className={cn(secondaryText, 'text-[14px] leading-[18px] mb-4')}>
            {t('staff.staffCount', { count: filteredAndSortedStaff.length })}
          </p>

          <div className="space-y-5">
            {filteredAndSortedStaff.length === 0 ? (
              <div className={cn('text-center py-12 border', whiteBG, rounded, borderGrey)}>
                <p className={cn(secondaryText, baseTextSize)}>
                  {staff.length === 0 ? t('staff.noStaffYet') : t('staff.noMatchingStaff')}
                </p>
              </div>
            ) : (
              filteredAndSortedStaff.map((user) => {
                const isSelected = selectedUserId === user.id;
                return (
                  <div key={user.id} className="space-y-3">
                    {/* Mobile: hide the list card while expanded so the header isn't duplicated */}
                    <div className={cn(isSelected && 'hidden lg:block')}>
                      <StaffUserCard
                        user={user}
                        restaurants={restaurants}
                        isSelected={isSelected}
                        canManage={canManageStaffMember(backendUser, user, actorRestaurantIds)}
                        onSelect={() => setSelectedUserId((prev) => (prev === user.id ? null : user.id))}
                      />
                    </div>
                    {isSelected && selectedUser && (
                      <div className="lg:hidden">{renderDetailPanel(selectedUser)}</div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>

        {/* Desktop: sticky side panel */}
        {/* Sticky offsets are measured against <main id="mainContainer">, the app's
            scroll container (App.tsx), which already starts below the NavBar — so top
            only needs the page gutter, not the navbar height. The height subtracts the
            navbar (~5rem) plus a gutter top and bottom (2 × 1.5rem) so the pinned panel
            ends inside the scrollport instead of running past its bottom edge. */}
        {selectedUser && (
          <div className="hidden lg:block min-w-0 lg:sticky lg:top-6 lg:self-start lg:h-[calc(100vh-8rem)] lg:max-h-[calc(100vh-8rem)]">
            {renderDetailPanel(selectedUser)}
          </div>
        )}
      </div>

      {/* Modals — kept mounted so the open/close slide transitions can run */}
      <SaveStaffUserModal
        isOpen={isCreateOpen}
        onClose={() => setCreateOpen(false)}
        onSaved={() => {
          setCreateOpen(false);
          reload();
        }}
      />

      {selectedStaff && (
        <ManageStaffRestaurantsModal
          isOpen={isManageOpen}
          staff={selectedStaff}
          onClose={() => setManageOpen(false)}
          onAdded={() => {
            setManageOpen(false);
            reload();
          }}
        />
      )}

      <ManageStaffAccessModal
        isOpen={isAccessOpen}
        staff={accessModalUser}
        restaurantId={accessModalRestaurantId}
        onClose={() => setAccessOpen(false)}
        onUpdated={() => {
          setAccessOpen(false);
          reload();
        }}
      />

      <DeleteUserModal
        isOpen={isDeleteOpen}
        user={deletingUser}
        onClose={() => setDeleteOpen(false)}
        onConfirm={handleDeleteUserConfirm}
      />

      <RemoveAccessModal
        isOpen={isRemoveAccessOpen}
        userEmail={removingAccess?.user.email ?? ''}
        restaurantName={removingAccess?.restaurantName ?? ''}
        onClose={() => setRemoveAccessOpen(false)}
        onConfirm={handleRemoveAccessConfirm}
      />
    </div>
  );
};

export default StaffPage;
